import _ from 'underscore';

import { applyCustomerHierarchyColumnFilter } from '../helper/customer-hierarchy-helper';
import { isInManagementTab } from '../helper/management-dashboard-helper';

export function getSharedColumnConfiguration(globalConfig) {
  const {
    IsAccountDashboardAlertsColumnAllowed,
    IsCustomerHierarchyEnabled,
    IsManagementDashboardEnabled,
    IsAudienceSOVEnabled,
    IsAccountLabelsEnabled,
    IsViewThroughConversionsRevenueEnabled,
    dynamic = {},
  } = globalConfig.get('Permissions');

  const {
    PartialConversions: IsPartialConversionsEnabled,
    ColumnConversion: IsColumnConversionEnabled,
    ConvValueRules: isConversionValueRuleColumnsEnabled,
    NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
    CBTLifetimeBudget: IsCbtLifetimeBudgetEnabled,
  } = dynamic;
  const usePartialConversions = IsPartialConversionsEnabled && IsColumnConversionEnabled;

  const columnNames = {
    AccountInactiveReasons: 'AccountInactiveReasons',
    AccountName: 'AccountName',
    AccountMode: 'AccountMode',
    PaymentSetting: 'PaymentSetting',
    Balance: 'Balance',
    Currency: 'Currency',
    BillingLanguage: 'BillingLanguage',
    BusinessLocation: 'BusinessLocation',
    AccountTimeZone: 'AccountTimeZone',
    TaxId: 'TaxId',
    PaymentMethod: 'PaymentMethod',
    DailyBudget: 'DailyBudget',
    CampaignLifetimeBudget: 'CampaignLifetimeBudget',
    AutoTagging: 'AutoTagging',
    PrimaryContact: 'PrimaryContact',
    Actions: 'Actions',
    Labels: 'Labels',
  };

  const lockedForVisualRefresh = [columnNames.AccountInactiveReasons, columnNames.AccountName];
  const lockedForLegacy = [columnNames.AccountName, columnNames.AccountInactiveReasons];

  // TODO: need to clean up other logic with IsAccountDashboardAlertsColumnAllowed since it's GAed.
  const locked = IsManagementDashboardEnabled ? lockedForVisualRefresh : lockedForLegacy;
  // NOTE: Please also make any changes to accounts-grid.es and PerformanceGridColumnConfiguration.cs
  // The config in PerformanceGridColumnConfiguration.cs is stored in PageContext.GridPrefetchQuery and not used in client-side code anywhere. So no need to change this file.

  function getAccountsSummaryColumns() {
    const summaryColumns = {
      AccountName: ['Name', 'AccountNumber', 'Id'],
      AccountMode: ['AccountMode'],
      Clicks: ['PerformanceMetrics/Clicks', 'AvailableInsightTypeIds'],
      Impressions: ['PerformanceMetrics/Impressions', 'AvailableInsightTypeIds'],
      CTR: 'PerformanceMetrics/CTR',
      AverageCPC: 'PerformanceMetrics/AverageCPC',
      AverageCPM: 'PerformanceMetrics/AverageCPM',
      VideoViews: 'PerformanceMetrics/VideoViews',
      ViewThroughRate: 'PerformanceMetrics/ViewThroughRate',
      AverageCPV: 'PerformanceMetrics/AverageCPV',
      TotalWatchTimeInMS: 'PerformanceMetrics/TotalWatchTimeInMS',
      AverageWatchTimePerImpression: 'PerformanceMetrics/AverageWatchTimePerImpression',
      AverageWatchTimePerVideoView: 'PerformanceMetrics/AverageWatchTimePerVideoView',
      VideoViewsAt25Percent: 'PerformanceMetrics/VideoViewsAt25Percent',
      VideoViewsAt50Percent: 'PerformanceMetrics/VideoViewsAt50Percent',
      VideoViewsAt75Percent: 'PerformanceMetrics/VideoViewsAt75Percent',
      CompletedVideoViews: 'PerformanceMetrics/CompletedVideoViews',
      VideoCompletionRate: 'PerformanceMetrics/VideoCompletionRate',
      AveragePosition: 'PerformanceMetrics/AveragePosition',
      AdvertiserReportedRevenue: 'PerformanceMetrics/AdvertiserReportedRevenue',
      RevenueOnAdSpend: 'PerformanceMetrics/RevenueOnAdSpend',
      AllConversionRevenueOnAdSpend: 'PerformanceMetrics/AllConversionRevenueOnAdSpend',
      AllConversionAdvertiserReportedRevenue: 'PerformanceMetrics/AllConversionAdvertiserReportedRevenue',
      TotalEffectiveCost: ['PerformanceMetrics/Spend', 'AvailableInsightTypeIds'],
      IOPurchaseOrder:
        'InsertionOrderDetails/CurrentInsertionOrder/PurchaseOrder',
      IOBudget: 'InsertionOrderDetails/CurrentInsertionOrder/Budget',
      RemainingIOBudget:
        'InsertionOrderDetails/CurrentInsertionOrder/RemainingBudget',
      StartDate: 'InsertionOrderDetails/CurrentInsertionOrder/StartDate',
      EndDate: 'InsertionOrderDetails/CurrentInsertionOrder/EndDate',
      DailyBudget: 'Budget/DailyBudget',
      QualityScore: 'QualityScoreData/OverallQualityScore',
      TotalRemainingIOBudget: 'InsertionOrderDetails/TotalRemainingBudget',
      OverallEndDate: 'InsertionOrderDetails/OverallEndDate',
    };

    if (isConversionValueRuleColumnsEnabled) {
      summaryColumns.AllConversionAdvertiserReportedRevenueAdjustment = 'PerformanceMetrics/AllConversionAdvertiserReportedRevenueAdjustment';
      summaryColumns.AdvertiserReportedRevenueAdjustment = 'PerformanceMetrics/AdvertiserReportedRevenueAdjustment';
      summaryColumns.ViewThroughConversionsRevenueAdjustment = 'PerformanceMetrics/ViewThroughConversionsRevenueAdjustment';
    }

    if (usePartialConversions) {
      summaryColumns.ViewThroughConversionsCredit = 'PerformanceMetrics/ViewThroughConversionsCredit';
      summaryColumns.ConversionsCredit = 'PerformanceMetrics/ConversionsCredit';
      summaryColumns.PartialConversionCPA = 'PerformanceMetrics/PartialConversionCPA';
      summaryColumns.PartialConversionRate = 'PerformanceMetrics/PartialConversionRate';
      summaryColumns.AllConversionCPA = 'PerformanceMetrics/AllConversionCPA';
      summaryColumns.AllConversionsCredit = 'PerformanceMetrics/AllConversionsCredit';
      summaryColumns.AllPartialConversionRate = 'PerformanceMetrics/AllPartialConversionRate';
    } else {
      summaryColumns.ViewThroughConversions = 'PerformanceMetrics/ViewThroughConversions';
      summaryColumns.Conversions = 'PerformanceMetrics/Conversions';
      summaryColumns.CPA = 'PerformanceMetrics/CPA';
      summaryColumns.ConversionRate = 'PerformanceMetrics/ConversionRate';
      summaryColumns.AllConversionCPA = 'PerformanceMetrics/AllConversionCPA';
      summaryColumns.AllConversions = 'PerformanceMetrics/AllConversions';
      summaryColumns.AllConversionRate = 'PerformanceMetrics/AllConversionRate';
    }
    if (IsAccountLabelsEnabled) {
      summaryColumns.Labels = ['Labels'];
    }

    if (IsViewThroughConversionsRevenueEnabled) {
      summaryColumns.ViewThroughConversionsRevenue = 'PerformanceMetrics/ViewThroughConversionsRevenue';
      summaryColumns.ViewThroughConversionsReturnOnAdSpend = 'PerformanceMetrics/ViewThroughConversionsReturnOnAdSpend';

      if (usePartialConversions) {
        summaryColumns.ViewThroughPartialConversionsCPA = 'PerformanceMetrics/ViewThroughPartialConversionsCPA';
        summaryColumns.ViewThroughPartialConversionsRate = 'PerformanceMetrics/ViewThroughPartialConversionsRate';
      } else {
        summaryColumns.ViewThroughConversionsCPA = 'PerformanceMetrics/ViewThroughConversionsCPA';
        summaryColumns.ViewThroughConversionsRate = 'PerformanceMetrics/ViewThroughConversionsRate';
      }
    }

    if (dynamic.CPSReporting) {
      summaryColumns.SalesCount = 'PerformanceMetrics/SalesCount';
    }

    if (dynamic.InstallPerformanceColumns) {
      summaryColumns.Installs = 'PerformanceMetrics/Installs';
    }

    if (isNewCustomerAcquisitionEnabled) {
      summaryColumns.NewCustomerConversionsRate = 'PerformanceMetrics/NewCustomerConversionRate';
      summaryColumns.NewCustomerConversions = 'PerformanceMetrics/NewCustomerConversions';
      summaryColumns.NewCustomerConversionsCPA = 'PerformanceMetrics/NewCustomerCPA';
      summaryColumns.NewCustomerConversionsValue = 'PerformanceMetrics/NewCustomerRevenue';
      summaryColumns.NewCustomerConversionsReturnOnAdSpend = 'PerformanceMetrics/NewCustomerRevenueOnAdSpend';
    }

    if (IsCbtLifetimeBudgetEnabled) {
      summaryColumns.CampaignLifetimeBudget = 'Budget/LifetimeBudget';
    }

    return summaryColumns;
  }

  function getSimpleColumns() {
    let simpleNameColumns = applyCustomerHierarchyColumnFilter(
      ['Currency', 'CompanyName', 'BillToCustomerName'],
      IsCustomerHierarchyEnabled
    );
    if (IsManagementDashboardEnabled) {
      simpleNameColumns = _.reject(simpleNameColumns, col => col === 'CompanyName');
    }

    return _.object(simpleNameColumns, simpleNameColumns);
  }

  function getSOVColumns() {
    return {
      ImpShare: 'PerformanceMetrics/AuctionWonPercent',
      ImpShareLostToBudget: 'PerformanceMetrics/AuctionLostToBudgetPercent',
      ImpShareLostToRank: 'PerformanceMetrics/AuctionLostToRankPercent',
      ImpShareLostToAdRelevance:
        'PerformanceMetrics/AuctionLostToLandingPercent',
      ImpShareLostToExpectedCTR:
        'PerformanceMetrics/AuctionLostToAdQualityPercent',
      ImpShareLostToBid: 'PerformanceMetrics/AuctionLostToBidPercent',
      ClickSharePercent: 'PerformanceMetrics/ClickSharePercent',
      AbsoluteTopImpressionSharePercent: 'PerformanceMetrics/AbsoluteTopImpressionSharePercent',
    };
  }

  function getSOVTopISColumns() {
    return {
      TopImpressionRate: 'PerformanceMetrics/TopImpressionRate',
      AbsoluteTopImpressionRate: 'PerformanceMetrics/AbsoluteTopImpressionRate',
      ExactMatchImpressionSharePercent: 'PerformanceMetrics/ExactMatchImpressionSharePercent',
      TopImpressionSharePercent: 'PerformanceMetrics/TopImpressionSharePercent',
      TopImpressionShareLostToBudget: 'PerformanceMetrics/TopISLostToBudgetPercent',
      TopImpressionShareLostToRank: 'PerformanceMetrics/TopISLostToRankPercent',
      AbsoluteTopImpressionShareLostToBudget: 'PerformanceMetrics/AbsoluteTopISLostToBudgetPercent',
      AbsoluteTopImpressionShareLostToRank: 'PerformanceMetrics/AbsoluteTopISLostToRankPercent',
    };
  }

  function getAudienceSOVColumns() {
    return {
      AudienceImpressionShare: 'PerformanceMetrics/AudienceAuctionWonPercent',
      AudienceImpressionShareLostToBudget: 'PerformanceMetrics/AudienceTopISLostToBudgetPercent',
      AudienceImpressionShareLostToRank: 'PerformanceMetrics/AudienceTopISLostToRankPercent',
    };
  }

  function columnsMap() {
    const statusColumn = IsAccountDashboardAlertsColumnAllowed ? {
      AccountInactiveReasons: ['Id', 'AccountInactiveReasons', 'AccountInactiveReasonsCount', 'BillToCustomerId'],
    } : { Status: 'Status' };

    const lockedColumnsMap = _.object(locked, locked);

    return _.chain(getAccountsSummaryColumns())
      .extend(getSimpleColumns())
      .extend(getSOVColumns())
      .extend(IsAudienceSOVEnabled ? getAudienceSOVColumns() : {})
      .extend(getSOVTopISColumns())
      .extend(statusColumn)
      .defaults(lockedColumnsMap)
      .value();
  }

  /* Used only in performance grid */
  function defaultColumns(gridName) {
    const isPerformanceGrid = gridName === 'PerformanceGrid';

    let defaultCols = locked.concat([
      'Currency',
      'AccountMode',
    ]).concat(applyCustomerHierarchyColumnFilter(isPerformanceGrid ? _.compact([
      'Clicks',
      'Impressions',
      'CTR',
      'AverageCPC',
      'TotalEffectiveCost',
      isPerformanceGrid && usePartialConversions ? 'ConversionsCredit' : 'Conversions',
      'TopImpressionRate',
      'AbsoluteTopImpressionRate',
    ]) : [
      'CompanyName',
      'BillToCustomerName',
      'DailyBudget',
      IsCbtLifetimeBudgetEnabled && 'CampaignLifetimeBudget',
    ], IsCustomerHierarchyEnabled));

    if (IsManagementDashboardEnabled) {
      defaultCols = _.reject(defaultCols, col => col === 'CompanyName');
    }
    return defaultCols;
  }

  // this function is also used in old ui data-fetch-config.js
  function getLockedColumns({ gridType, isForVNext } = {}) {
    if (isInManagementTab(gridType, () => globalConfig) && !isForVNext) {
      return locked.concat(columnNames.Actions);
    }
    if (isForVNext) {
      // TODO: cleanup this logic after ReactRouter GA everywhere
      // This is for columns related JS Selenium test suite
      // For phase 2 we always assume that ReactRouter is GAed
      // under Onebox we enable ReactRouter pilot, but in SI not
      // Fixing this gap does not affect Prod as ReactRouter is GAed (with some exceptions) in Prod
      return lockedForVisualRefresh;
    }
    return locked;
  }

  return {
    columnNames,
    columnsMap,
    defaultColumns,
    getLockedColumns,
  };
}
